import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import ImageLightbox from '../components/ImageLightbox/ImageLightbox';
import PropertyVisual from '../components/PropertyVisual';
import { useLanguage } from '../context/LanguageContext';
import { getSaleListing } from '../data/sales';
import { useImageLightbox } from '../hooks/useImageLightbox';

function numericFact(value) {
  return String(value).match(/\d+(?:[.,]\d+)?/)?.[0] ?? value;
}

function SaleFactIcon({ name, className = 'sale-fact__icon' }) {
  const iconProps = {
    'aria-hidden': true,
    className,
    fill: 'none',
    viewBox: '0 0 24 24',
  };

  switch (name) {
    case 'floor':
      return (
        <svg {...iconProps}>
          <rect x="4" y="3" width="16" height="18" rx="1.5" />
          <path d="M4 9h16M4 15h16M9 21v-6h6v6" />
        </svg>
      );
    case 'area':
      return (
        <svg {...iconProps}>
          <path d="M9 4H4v5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
          <path d="m4 9 5-5M15 4l5 5M20 15l-5 5M9 20l-5-5" />
        </svg>
      );
    case 'bedrooms':
      return (
        <svg {...iconProps}>
          <path d="M3 19v-9M21 19v-6H3v6M6 13V8h5a3 3 0 0 1 3 3v2M3 17h18" />
        </svg>
      );
    case 'bathrooms':
      return (
        <svg {...iconProps}>
          <path d="M4 13h16v2a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5v-2ZM7 13V6a3 3 0 0 1 5.5-1.65" />
          <path d="M4 20v1M20 20v1" />
        </svg>
      );
    case 'parking':
      return (
        <svg {...iconProps}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M10 17V7h3a3 3 0 0 1 0 6h-3" />
        </svg>
      );
    case 'atrium':
      return (
        <svg {...iconProps}>
          <path d="M12 21V10" />
          <path d="M12 14C8 14 5 11.5 5 7c4 0 7 2.5 7 7ZM12 11c0-4 2.6-7 7-7 0 4.2-2.6 7-7 7Z" />
        </svg>
      );
    case 'projector':
      return (
        <svg {...iconProps}>
          <rect x="3" y="6" width="18" height="11" rx="2" />
          <circle cx="16.5" cy="11.5" r="2.5" />
          <path d="M6 17v3M18 17v3M7 10h4" />
        </svg>
      );
    case 'terrace':
      return (
        <svg {...iconProps}>
          <path d="M3 18h18M6 18V9h12v9M9 18v-5h6v5" />
          <circle cx="18" cy="5" r="2" />
          <path d="M18 1v1M18 8v1M14 5h1M21 5h1" />
        </svg>
      );
    case 'living':
      return (
        <svg {...iconProps}>
          <path d="M5 12V9a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
          <path d="M3 11a2 2 0 0 1 2 2v3h14v-3a2 2 0 1 1 2 0v6H3v-6a2 2 0 0 1 0-2ZM6 19v2M18 19v2" />
        </svg>
      );
    case 'kitchen':
      return (
        <svg {...iconProps}>
          <path d="M7 3v7M4 3v4a3 3 0 0 0 6 0V3M7 10v11M16 3v18M16 3c3 2 4 5 4 8h-4" />
        </svg>
      );
    case 'storage':
      return (
        <svg {...iconProps}>
          <path d="M4 5h16v16H4zM4 10h16M9 5v16" />
          <path d="M6.5 13h1M11.5 13h6M11.5 17h6" />
        </svg>
      );
    default:
      return null;
  }
}

function SaleApartmentPage() {
  const { apartmentNumber } = useParams();
  const { language } = useLanguage();
  const listing = getSaleListing(apartmentNumber);
  const residence = listing?.residence;

  const galleryImages = useMemo(() => residence?.gallery.map((src, index) => ({
    id: `sale-${apartmentNumber}-${index + 1}`,
    src,
    alt: language === 'ru'
      ? `Апартамент №${apartmentNumber} — фотография ${index + 1}`
      : `Apartment ${apartmentNumber} — photo ${index + 1}`,
  })) ?? [], [apartmentNumber, language, residence]);
  const galleryPreview = galleryImages.slice(0, 8);
  const lightbox = useImageLightbox(galleryImages);

  if (!listing || !residence) {
    return <Navigate to="/sale" replace />;
  }

  const copy = language === 'ru'
    ? {
        forSale: 'В продаже',
        apartment: 'Апартамент',
        back: 'Все объекты в продаже',
        location: 'Святой‑Стефан · Черногория',
        overview: 'Дом в Adriatic Masterpiece Residence',
        arrangeViewing: 'Договориться о просмотре',
        facts: [
          ['area', 'Площадь', residence.specs.ru[1]],
          ['floor', 'Этаж', numericFact(listing.floor.ru)],
          ['bedrooms', 'Спальни', numericFact(residence.specs.ru[2])],
          ['bathrooms', 'Санузлы', numericFact(residence.specs.ru[3])],
          ['parking', 'Парковка', 'Включена в стоимость'],
          ['storage', 'Кладовая', 'Включена в стоимость'],
        ],
        storyEyebrow: 'Характер пространства',
        highlights: 'Почему стоит обратить внимание',
        floorPlan: 'Планировка',
        floorPlanPlaceholder: 'Планировка апартамента №32',
        gallery: 'Посмотрите апартамент',
        galleryLead: 'Интерьеры, террасы и детали пространства',
        viewAllPhotos: `Смотреть все фотографии · ${galleryImages.length}`,
        contactEyebrow: 'Условия продажи',
        contactTitle: 'Grand 3BD в Adriatic Masterpiece Residence',
        contactLead: 'Парковочное место и приватная кладовая включены в стоимость апартамента.',
      }
    : {
        forSale: 'For sale',
        apartment: 'Apartment',
        back: 'All properties for sale',
        location: 'Sveti Stefan · Montenegro',
        overview: 'A home at Adriatic Masterpiece Residence',
        arrangeViewing: 'Arrange a private viewing',
        facts: [
          ['area', 'Area', residence.specs.en[1]],
          ['floor', 'Floor', numericFact(listing.floor.en)],
          ['bedrooms', 'Bedroom', numericFact(residence.specs.en[2])],
          ['bathrooms', 'Bathroom', numericFact(residence.specs.en[3])],
          ['parking', 'Parking', 'Included in the price'],
          ['storage', 'Storage', 'Included in the price'],
        ],
        storyEyebrow: 'The character of the home',
        highlights: 'Why this apartment stands out',
        floorPlan: 'Floor plan',
        floorPlanPlaceholder: 'Floor plan of apartment 32',
        gallery: 'See the apartment',
        galleryLead: 'Interiors, terraces and details of the living space',
        viewAllPhotos: `View all photographs · ${galleryImages.length}`,
        contactEyebrow: 'Sale terms',
        contactTitle: 'Grand 3BD at Adriatic Masterpiece Residence',
        contactLead: 'A private parking space and storage room are included in the apartment price.',
      };

  return (
    <article className="sale-detail-page">
      <section className="sale-detail-hero">
        <PropertyVisual src={residence.image} alt={residence.name[language]} eager />
        <div className="sale-detail-hero__title">
          <span className="eyebrow eyebrow_light">{copy.forSale} · {copy.apartment} № {listing.apartmentNumber} · {listing.floor[language]}</span>
          <h1>{residence.name[language]}</h1>
          <p>{listing.heroLine[language]}</p>
          <div className="sale-detail-hero__actions">
            <a className="button button_light" href="#purchase-enquiry">{listing.price[language]}</a>
            <span>{copy.location}</span>
          </div>
        </div>
      </section>

      <section className="section sale-overview">
        <Link className="text-link sale-overview__back" to="/sale">← {copy.back}</Link>

        <aside className="sale-overview__aside">
          <div className="sale-overview__offer">
            <span className="sale-overview__location">{copy.location}</span>
            <div className="sale-overview__price">
              <span>{copy.forSale}</span>
              <strong>{listing.price[language]}</strong>
            </div>
            <a className="text-link" href="#purchase-enquiry">{copy.arrangeViewing} <span>↗</span></a>
          </div>
        </aside>

        <div className="sale-overview__content">
          <span className="eyebrow">{copy.overview}</span>
          <p className="sale-overview__lead">{listing.lead[language]}</p>
        </div>

        <dl className="sale-facts">
          {copy.facts.map(([icon, label, value]) => (
            <div key={label}>
              <dt>
                <SaleFactIcon name={icon} />
                <span>{label}</span>
              </dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="section section_wide sale-story">
        <div className="sale-story__visual">
          <PropertyVisual src={residence.gallery[1] ?? residence.image} alt={residence.name[language]} />
        </div>
        <div className="sale-story__copy">
          <span className="eyebrow">{copy.storyEyebrow}</span>
          <h2>{listing.storyTitle[language]}</h2>
          {listing.story[language].map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </section>

      <section className="section sale-highlights">
        <div className="section-heading">
          <h2>{copy.highlights}</h2>
        </div>
        <ul>
          {listing.highlights[language].map((highlight, index) => (
            <li key={highlight}>
              <SaleFactIcon name={listing.highlightIcons[index]} className="sale-highlight__icon" />
              <strong>{highlight}</strong>
            </li>
          ))}
        </ul>
      </section>

      <section className="section section_wide residence-gallery-section sale-gallery-section">
        <div className="section-heading section-heading_split">
          <h2>{copy.gallery}</h2>
          <p>{copy.galleryLead}</p>
        </div>
        <div className="residence-gallery">
          {galleryPreview.map((image, index) => (
            <button
              className="residence-gallery__item sale-gallery__item"
              type="button"
              key={image.id}
              onClick={() => lightbox.open(index)}
              aria-label={language === 'ru' ? `Открыть: ${image.alt}` : `Open ${image.alt}`}
            >
              <img src={image.src} alt={image.alt} loading={index < 3 ? 'eager' : 'lazy'} decoding="async" />
            </button>
          ))}
        </div>
        {galleryImages.length > galleryPreview.length && (
          <button className="text-link sale-gallery__all" type="button" onClick={() => lightbox.open(0)}>
            {copy.viewAllPhotos} <span>↗</span>
          </button>
        )}
      </section>

      <section className="section section_wide sale-floor-plan">
        <div className="section-heading">
          <h2>{copy.floorPlan}</h2>
        </div>
        <figure className="sale-floor-plan__figure">
          <img src={listing.floorPlanImage} alt={copy.floorPlanPlaceholder} loading="lazy" decoding="async" />
        </figure>
      </section>

      <section className="sale-contact-cta" id="purchase-enquiry">
        <span className="eyebrow">{copy.contactEyebrow}</span>
        <h2>{copy.contactTitle}</h2>
        <p>{copy.contactLead}</p>
        <strong className="sale-contact-cta__price">{listing.price[language]}</strong>
      </section>

      <ImageLightbox
        activeIndex={lightbox.activeIndex}
        images={galleryImages}
        isOpen={lightbox.isOpen}
        onClose={lightbox.close}
      />
    </article>
  );
}

export default SaleApartmentPage;
